import { Button } from '@rental/ui'
import { Search } from 'lucide-react'
import { type FormEvent, type ReactNode, type Ref, useId, useRef, useState } from 'react'
import { useLocale } from '../i18n'
import { StaffInput } from './ui'

/**
 * A list's search (DESIGN.md, "A screen and its parts"): its fields, Cerca,
 * and Azzera while a filter is set, in one row that wraps. Every control of
 * the row lines up with the buttons, so no field puts a hint under its box:
 * the text field says what it finds beside its label. Azzera goes with the
 * filter it clears, so it gives the focus to the first field, where the next
 * search starts (UI-2g): left on nothing, it fell back to the page's start.
 */
export function SearchForm({
  label,
  submitLabel,
  filtered,
  onSubmit,
  onClear,
  options,
  children,
}: {
  /** The landmark's name: the screen's title. The menu has a search of its own. */
  label: string
  /** A period shows rather than searches (Fiscale, Report: "Mostra", UI-2e); without it, Cerca. */
  submitLabel?: string
  filtered: boolean
  onSubmit: () => void
  onClear: () => void
  /** Switches that search the moment they change: after Cerca, which they do not need. */
  options?: ReactNode
  children: ReactNode
}) {
  const { messages: m } = useLocale()
  const form = useRef<HTMLFormElement>(null)
  function submit(event: FormEvent) {
    event.preventDefault()
    onSubmit()
  }
  return (
    // <search> came with Safari 17; on iPadOS 16.4 it would name no landmark.
    // biome-ignore lint/a11y/useSemanticElements: the role reaches every supported browser
    <form
      ref={form}
      role="search"
      aria-label={label}
      onSubmit={submit}
      className="flex flex-wrap items-end gap-4"
    >
      {children}
      <div className="flex flex-wrap gap-3">
        <Button type="submit" variant="staff">
          {submitLabel ?? (
            <>
              <Search aria-hidden="true" className="size-4" />
              {m.bookings.submit}
            </>
          )}
        </Button>
        {filtered ? (
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => {
              onClear()
              form.current
                ?.querySelector<HTMLElement>(
                  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
                )
                ?.focus()
            }}
          >
            {m.bookings.clear}
          </Button>
        ) : null}
      </div>
      {options}
    </form>
  )
}

/**
 * The words a list is searched by: its label, what it finds beside it, and the
 * box. Inside another form (Nuova prenotazione's customer, UI-2d) Enter is the
 * search's own (`onEnter`): that form is sent by its button alone.
 */
export function SearchField({
  label,
  hint,
  value,
  onChange,
  onEnter,
  inputRef,
}: {
  label: string
  hint: string
  value: string
  onChange: (value: string) => void
  onEnter?: () => void
  inputRef?: Ref<HTMLInputElement>
}) {
  const id = useId()
  return (
    <div className="flex min-w-0 grow basis-3xs flex-col gap-1">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <label htmlFor={id} className="text-sm font-medium text-text">
          {label}
        </label>
        <span id={`${id}-hint`} className="text-xs text-text-muted">
          {hint}
        </span>
      </div>
      <StaffInput
        id={id}
        ref={inputRef}
        aria-describedby={`${id}-hint`}
        type="search"
        enterKeyHint="search"
        autoComplete="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={
          onEnter
            ? (event) => {
                if (event.key !== 'Enter') return
                event.preventDefault()
                onEnter()
              }
            : undefined
        }
      />
    </div>
  )
}

/**
 * A search form's fields, kept in step with the address. What is typed stays
 * until it is searched; a search changed from outside the form ("Trova" in the
 * menu, Back, a link) replaces it, so the form never shows one search while
 * the list shows another.
 */
export function useSearchFields<T extends Readonly<Record<string, string>>>(
  asked: T,
): [T, (fields: T) => void] {
  const [fields, setFields] = useState(asked)
  const askedKey = JSON.stringify(asked)
  const [shown, setShown] = useState(askedKey)
  if (shown !== askedKey) {
    setShown(askedKey)
    setFields(asked)
  }
  return [fields, setFields]
}
